<script setup>
/** 五个阶段按发生顺序排开，时间各取自记录它的那张表，相邻阶段之间标出耗时。 */
import { computed } from 'vue'
import { Badge } from '@drama/ui'
import { useI18n } from 'vue-i18n'

import { formatDate, formatDuration, formatElapsed, formatOptional, formatSize, statusVariant } from '../generation-records-format.js'

const props = defineProps({
  process: { type: Object, required: true },
})

const { t } = useI18n()

const stages = computed(() => {
  const { run, video_request: video, screenplay, production, final_video: finalVideo } = props.process
  const storedSegments = production === null ? 0 : production.segments.filter(segment => segment.clip !== null).length
  const totalScenes = screenplay === null ? 0 : screenplay.scenes.length
  const failedBeforeScreenplay = video.status === 'failed' && screenplay === null
  const failedInProduction = video.status === 'failed' && screenplay !== null
  return [
    {
      key: 'run',
      state: 'done',
      at: run?.completed_at ?? run?.created_at ?? null,
      note: run === null ? null : t('visualGameAdmin.generationRecords.process.stageNote.turns', { count: run.turn_no }),
    },
    {
      key: 'requested',
      state: 'done',
      at: video.requested_at,
      note: video.provider,
    },
    {
      key: 'screenplay',
      state: screenplay !== null ? 'done' : failedBeforeScreenplay ? 'failed' : 'pending',
      at: screenplay?.created_at ?? (failedBeforeScreenplay ? video.completed_at : null),
      note: screenplay === null ? null : t('visualGameAdmin.generationRecords.process.stageNote.scenes', { count: totalScenes }),
    },
    {
      key: 'production',
      state: video.status === 'ready' ? 'done' : failedInProduction ? 'failed' : video.status === 'running' ? 'active' : 'pending',
      // 失败的视频 completed_at 记的是失败时间
      at: failedInProduction ? video.completed_at : video.started_at,
      note: totalScenes === 0 ? null : t('visualGameAdmin.generationRecords.process.stageNote.segments', { done: storedSegments, total: totalScenes }),
    },
    {
      key: 'assembly',
      state: video.status === 'ready' ? 'done' : 'pending',
      at: video.status === 'ready' ? video.completed_at : null,
      note: finalVideo === null ? null : `${formatDuration(finalVideo.duration_ms)} · ${formatSize(finalVideo.size)}`,
    },
  ]
})

const failedStage = computed(() => stages.value.find(stage => stage.state === 'failed') ?? null)
</script>

<template>
  <div class="process-overview">
    <ol class="process-overview__stages">
      <template v-for="(stage, index) in stages" :key="stage.key">
        <li v-if="index > 0" :class="['process-overview__link', { 'is-reached': stage.state !== 'pending' }]">
          <span>{{ formatElapsed(stages[index - 1].at, stage.at) }}</span>
        </li>
        <li :class="['process-overview__stage', `is-${stage.state}`]">
          <span class="process-overview__dot" />
          <strong>{{ t(`visualGameAdmin.generationRecords.process.stages.${stage.key}`) }}</strong>
          <span class="process-overview__at">{{ formatDate(stage.at) }}</span>
          <span class="process-overview__note">{{ stage.note ?? ' ' }}</span>
        </li>
      </template>
    </ol>

    <p v-if="failedStage && process.video_request.error_message" class="process-overview__error">
      <strong>{{ t(`visualGameAdmin.generationRecords.process.stages.${failedStage.key}`) }}</strong>
      {{ process.video_request.error_message }}
    </p>

    <dl class="process-overview__facts">
      <div>
        <dt>{{ t('visualGameAdmin.generationRecords.process.facts.status') }}</dt>
        <dd><Badge :variant="statusVariant(process.video_request.status)" size="sm">{{ t(`visualGameAdmin.generationRecords.status.${process.video_request.status}`) }}</Badge></dd>
      </div>
      <div>
        <dt>{{ t('visualGameAdmin.generationRecords.process.facts.ending') }}</dt>
        <dd>{{ process.run?.ending_title ?? process.run?.ending_id ?? '-' }}</dd>
      </div>
      <div>
        <dt>{{ t('visualGameAdmin.generationRecords.process.facts.player') }}</dt>
        <dd>{{ formatOptional(process.run?.player_character ?? null) }}</dd>
      </div>
      <div>
        <dt>{{ t('visualGameAdmin.generationRecords.process.facts.version') }}</dt>
        <dd><code>{{ formatOptional(process.run?.scenario_version_id ?? null) }}</code></dd>
      </div>
      <div>
        <dt>{{ t('visualGameAdmin.generationRecords.process.facts.provider') }}</dt>
        <dd>{{ formatOptional(process.video_request.provider) }}</dd>
      </div>
      <div>
        <dt>{{ t('visualGameAdmin.generationRecords.process.facts.generationRequest') }}</dt>
        <dd><code>{{ formatOptional(process.video_request.generation_request_id) }}</code></dd>
      </div>
      <div v-if="process.screenplay?.authored_by">
        <dt>{{ t('visualGameAdmin.generationRecords.process.facts.author') }}</dt>
        <dd>{{ process.screenplay.authored_by }}</dd>
      </div>
    </dl>
  </div>
</template>

<style scoped>
.process-overview__stages {
  display: flex;
  align-items: flex-start;
  margin: 0;
  padding: var(--drama-space-lg) 0 0;
  list-style: none;
}

.process-overview__stage {
  position: relative;
  display: flex;
  flex: 0 0 148px;
  flex-direction: column;
  align-items: center;
  gap: var(--drama-space-xs);
  padding-top: var(--drama-space-xl);
  font-size: var(--drama-font-size-sm);
  text-align: center;
}

.process-overview__dot {
  position: absolute;
  top: 0;
  width: 14px;
  height: 14px;
  border: 2px solid var(--drama-color-border-strong);
  border-radius: 50%;
  background: var(--drama-color-surface);
}

.process-overview__stage.is-done .process-overview__dot {
  border-color: var(--drama-color-success);
  background: var(--drama-color-success);
}

.process-overview__stage.is-active .process-overview__dot {
  border-color: var(--drama-color-info);
  background: var(--drama-color-info);
  animation: process-overview-pulse 1.2s ease-in-out infinite;
}

.process-overview__stage.is-failed .process-overview__dot {
  border-color: var(--drama-color-danger);
  background: var(--drama-color-danger);
}

.process-overview__stage.is-pending {
  color: var(--drama-color-text-muted);
}

.process-overview__at,
.process-overview__note {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.process-overview__stage.is-active .process-overview__note {
  color: var(--drama-color-info);
}

/* 圆点直径 14px，连线压在圆点垂直中线上；耗时标签居中挂在线段上方 */
.process-overview__link {
  position: relative;
  flex: 1 1 0;
  height: 2px;
  margin: 6px -66px 0;
  background: var(--drama-color-border);
}

.process-overview__link.is-reached {
  background: var(--drama-color-success);
}

.process-overview__link span {
  position: absolute;
  bottom: var(--drama-space-sm);
  left: 50%;
  transform: translateX(-50%);
  color: var(--drama-color-text-muted);
  font-family: var(--drama-font-family-mono);
  font-size: var(--drama-font-size-xs);
  white-space: nowrap;
}

@keyframes process-overview-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

.process-overview__error {
  margin: var(--drama-space-xl) 0 0;
  padding: var(--drama-space-md) var(--drama-space-lg);
  border-left: 3px solid var(--drama-color-danger);
  background: color-mix(in srgb, var(--drama-color-danger) 6%, transparent);
  color: var(--drama-color-danger);
  font-size: var(--drama-font-size-sm);
  line-height: 1.6;
  word-break: break-word;
}

.process-overview__error strong {
  margin-right: var(--drama-space-sm);
}

.process-overview__facts {
  display: flex;
  flex-wrap: wrap;
  gap: var(--drama-space-md) var(--drama-space-2xl);
  margin: var(--drama-space-xl) 0 0;
  padding-top: var(--drama-space-lg);
  border-top: 1px solid var(--drama-color-border);
}

.process-overview__facts dt {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.process-overview__facts dd {
  margin: var(--drama-space-xs) 0 0;
  font-size: var(--drama-font-size-sm);
}

.process-overview__facts code {
  font-family: var(--drama-font-family-mono);
  font-size: var(--drama-font-size-xs);
}

@media (max-width: 960px) {
  .process-overview__stages {
    flex-direction: column;
    gap: var(--drama-space-md);
    padding-top: 0;
  }

  .process-overview__stage {
    flex-basis: auto;
    align-items: flex-start;
    padding: 0 0 0 var(--drama-space-xl);
    text-align: left;
  }

  .process-overview__dot {
    top: 3px;
    left: 0;
  }

  .process-overview__link {
    display: none;
  }
}
</style>
